import { useState } from "react";
import { Box, Button, Container, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isAfter, isValid } from "date-fns";
import { useWhitelabelStore } from "src/store/whitelabel/whitelabel.store";
import { usePublicRanking } from "../hooks/use-public-ranking";
import { useConfetti } from "../hooks/use-confetti";
import RankingContent from "./ranking-content";
import { useSettingsContext } from "src/components/settings";

// ----------------------------------------------------------------------

const today = new Date();

function toDateStr(date: Date | null): string | undefined {
  return date && isValid(date) ? format(date, "yyyy-MM-dd") : undefined;
}

export default function PublicRankingView() {
  const settings = useSettingsContext();

  // Estado de rascunho (o que está nos pickers)
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);

  // Estado aplicado (o que é enviado ao back)
  const [appliedStart, setAppliedStart] = useState<Date | null>(null);
  const [appliedEnd, setAppliedEnd] = useState<Date | null>(null);

  const { rankedSellers, rankedPositions, loading } = usePublicRanking({
    startDate: toDateStr(appliedStart),
    endDate: toDateStr(appliedEnd),
  });
  const whitelabel = useWhitelabelStore((state) => state.whitelabel);

  // Confetes - SOMENTE na URL "/dashboard/ranking"
  useConfetti(500);

  const endDateError =
    draftStart && isValid(draftStart) && draftEnd && isValid(draftEnd) && !isAfter(draftEnd, draftStart)
      ? "A data de fim deve ser maior que a de início"
      : undefined;

  const canApply =
    !endDateError &&
    draftStart !== null &&
    draftEnd !== null &&
    (draftStart !== appliedStart || draftEnd !== appliedEnd);

  const handleApply = () => {
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
  };

  const handleClear = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setAppliedStart(null);
    setAppliedEnd(null);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : "lg"} sx={{ py: 4 }}>
      {/* Logo whitelabel fixo no canto superior esquerdo */}
      {whitelabel?.logo && (
        <Box
          component="img"
          src={whitelabel.logo}
          alt={whitelabel.name}
          sx={{
            top: 16,
            left: 16,
            height: 54,
            width: "auto",
            objectFit: "contain",
            zIndex: 1300,
            pointerEvents: "none",
            mb: 4,
          }}
        />
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-start" }}
        sx={{ mb: 3 }}
      >
        <DatePicker
          label="Data de início"
          value={draftStart}
          onChange={(val) => {
            const next = val && isValid(val) ? val : null;
            setDraftStart(next);
            if (draftEnd && next && !isAfter(draftEnd, next)) {
              setDraftEnd(null);
            }
          }}
          maxDate={today}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          label="Data de fim"
          value={draftEnd}
          onChange={(val) => setDraftEnd(val && isValid(val) ? val : null)}
          minDate={draftStart && isValid(draftStart) ? draftStart : undefined}
          maxDate={today}
          slotProps={{
            textField: {
              size: "small",
              error: !!endDateError,
              helperText: endDateError,
            },
          }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="medium"
            onClick={handleApply}
            disabled={!canApply}
          >
            Aplicar
          </Button>
          <Button
            variant="outlined"
            size="medium"
            color="inherit"
            onClick={handleClear}
            disabled={!draftStart && !draftEnd && !appliedStart && !appliedEnd}
          >
            Limpar
          </Button>
        </Stack>
      </Stack>

      <RankingContent
        rankedSellers={rankedSellers}
        rankedPositions={rankedPositions}
        loading={loading}
        maxWidth={settings.themeStretch ? false : "lg"}
      />
    </Container>
  );
}
